// The test page: a print job for the MPS-803 made in the browser, handed to the C64 as a replay disk (replay.js).
// Printy McPrintface test page (centered), a black and white picture, "Print Successfully".
import { DOTS_PER_LINE } from '../core/mps803.js';

export const TITLE = 'Printy McPrintface test page';
export const FOOTER = 'Print Successfully';
export const CHANNEL = 7; // the business character set: upper and lower case

const CR = 13;
const BIT_IMAGE_ON = 8;
const BIT_IMAGE_OFF = 15; // also back to normal width; a CR does not leave the bit image mode
const REPEAT = 26;
const MPS_COLUMNS = 80;

// ASCII to PETSCII as the MPS-803 reads it in the business set: a-z are $41-$5A, A-Z are $C1-$DA.
export function petscii(text) {
  return Uint8Array.from(text, (ch) => {
    const c = ch.charCodeAt(0);
    if (c >= 0x61 && c <= 0x7a) return c - 0x20;
    if (c >= 0x41 && c <= 0x5a) return c + 0x80;
    return c > 0x7e ? 0x3f : c;
  });
}

export const centered = (text, columns = MPS_COLUMNS) => ' '.repeat(Math.max(0, Math.floor((columns - text.length) / 2))) + text;

// ---- the picture ----

// RGBA pixels to a black and white bitmap { width, height, data } with 1 = black (dark and not transparent).
export function thresholdRgba(rgba, width, height, level = 140) {
  const data = new Uint8Array(width * height);
  for (let i = 0; i < data.length; i++) {
    const a = rgba[i * 4 + 3] / 255;
    const luma = (0.299 * rgba[i * 4] + 0.587 * rgba[i * 4 + 1] + 0.114 * rgba[i * 4 + 2]) * a + 255 * (1 - a); // transparent = paper
    data[i] = luma < level ? 1 : 0;
  }
  return { width, height, data };
}

// Cuts the white margins off (keeps `pad` dots around the ink). An empty picture stays as it is.
export function cropToInk(bm, pad = 4) {
  let x0 = bm.width, x1 = -1, y0 = bm.height, y1 = -1;
  for (let y = 0; y < bm.height; y++) {
    for (let x = 0; x < bm.width; x++) {
      if (!bm.data[y * bm.width + x]) continue;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < 0) return bm;
  x0 = Math.max(0, x0 - pad);
  y0 = Math.max(0, y0 - pad);
  x1 = Math.min(bm.width - 1, x1 + pad);
  y1 = Math.min(bm.height - 1, y1 + pad);
  const width = x1 - x0 + 1;
  const height = y1 - y0 + 1;
  const data = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) data.set(bm.data.subarray((y0 + y) * bm.width + x0, (y0 + y) * bm.width + x0 + width), y * width);
  return { width, height, data };
}

// The printer's dots are 1/60" wide and 1/72" tall: to keep the proportions the picture gets 1.2 times as many rows
// as a square-pixel scaling would give. At most `maxWidth` dots wide; smaller pictures keep their size. Area average,
// then black where at least half of the covered pixels are black.
export function fitToPrinter(bm, maxWidth = DOTS_PER_LINE) {
  const scale = Math.min(1, maxWidth / bm.width);
  const width = Math.max(1, Math.round(bm.width * scale));
  const height = Math.max(1, Math.round(bm.height * scale * 1.2));
  const data = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    const sy0 = (y * bm.height) / height;
    const sy1 = ((y + 1) * bm.height) / height;
    for (let x = 0; x < width; x++) {
      const sx0 = (x * bm.width) / width;
      const sx1 = ((x + 1) * bm.width) / width;
      let black = 0, total = 0;
      for (let sy = Math.floor(sy0); sy < Math.min(bm.height, Math.ceil(sy1)); sy++) {
        const wy = Math.min(sy + 1, sy1) - Math.max(sy, sy0);
        for (let sx = Math.floor(sx0); sx < Math.min(bm.width, Math.ceil(sx1)); sx++) {
          const w = wy * (Math.min(sx + 1, sx1) - Math.max(sx, sx0));
          total += w;
          if (bm.data[sy * bm.width + sx]) black += w;
        }
      }
      data[y * width + x] = total > 0 && black * 2 >= total ? 1 : 0;
    }
  }
  return { width, height, data };
}

// The bit image lines of a bitmap: bands of 7 rows, one byte per column ($80 + the 7 pins, bit 0 on top), equal
// bytes in a row as `$1A count byte`, each band ended by CR. Starts with the bit image switch, ends by leaving the mode.
export function bitImage(bm) {
  const out = [];
  for (let y0 = 0; y0 < bm.height; y0 += 7) {
    out.push(BIT_IMAGE_ON);
    const cols = [];
    for (let x = 0; x < bm.width; x++) {
      let v = 0x80;
      for (let r = 0; r < 7; r++) if (y0 + r < bm.height && bm.data[(y0 + r) * bm.width + x]) v |= 1 << r;
      cols.push(v);
    }
    for (let x = 0; x < cols.length; ) {
      let run = 1;
      while (x + run < cols.length && cols[x + run] === cols[x] && run < 255) run++;
      if (run >= 4) out.push(REPEAT, run, cols[x]);
      else for (let k = 0; k < run; k++) out.push(cols[x]);
      x += run;
    }
    out.push(CR);
  }
  out.push(BIT_IMAGE_OFF);
  return Uint8Array.from(out);
}

// The events of the job (jobs.js): one transfer on the business channel.
export function testPrintEvents(picture) {
  const bytes = (...parts) => ({ t: 'data', bytes: Uint8Array.from(parts.flatMap((p) => [...p])) });
  const line = (text) => [petscii(text), Uint8Array.of(CR)];
  return [
    { t: 'listen', sa: 0x60 | CHANNEL },
    bytes(...line(''), ...line(centered(TITLE)), ...line('')),
    { t: 'data', bytes: bitImage(picture) },
    bytes(...line(''), ...line(FOOTER), ...line('')),
    { t: 'unlisten' },
  ];
}

// RGBA of the picture file in the browser (an <canvas> does the decoding).
export async function loadPicture(url, fetchFn = fetch) {
  const res = await fetchFn(url);
  if (!res.ok) throw new Error(`the test picture could not be loaded (${res.status})`);
  const bmp = await createImageBitmap(await res.blob());
  const canvas = Object.assign(document.createElement('canvas'), { width: bmp.width, height: bmp.height });
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bmp, 0, 0);
  const { data } = ctx.getImageData(0, 0, bmp.width, bmp.height);
  return fitToPrinter(cropToInk(thresholdRgba(data, bmp.width, bmp.height)));
}
